import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useApiRequestControllable } from '@europrocurement/l2d-modules';
import {
    AchatsAppDispatch,
    ReminderPublisher,
    useAchatsService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { ReminderPublisherApiDeleteReminderPublisherItemRequest } from '@europrocurement/l2d-domain/openApi/ApiAchats';
import { useTheme } from '@mui/material';
import { MessagesTypes } from '@europrocurement/flexy-components/constants/wording/modals';
import { useSnackbar } from 'notistack';

export type UseDeleteReminderPublisherProps = {
    from: 'list' | 'details';
};

/**
 * Hook personnalisé pour gérer la suppression d'un ou plusieurs rappels fournisseurs
 *
 * @param options - Options de configuration du hook
 * @returns Fonctions et état pour gérer la suppression des rappels
 */
const useDeleteReminderPublisher = ({ from }: UseDeleteReminderPublisherProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const xIdSociete = useSociete();
    const dispatch = useDispatch<AchatsAppDispatch>();
    const { request, requestState } = useApiRequestControllable();
    const { enqueueSnackbar } = useSnackbar();

    const { getReminderPublisher, reminderPublisherApi } = useAchatsService();
    /**
     * Génère les messages de confirmation appropriés en fonction du statut du rappel
     *
     * @param status - Statut actuel du rappel
     * @returns Objets de messages pour la confirmation
     */
    const getMessages = React.useCallback((status: ReminderPublisher['status']): MessagesTypes => {
        if (!status) return {};

        if (status === 'sent') {
            return {
                question:
                    'Êtes-vous sûr de vouloir supprimer définitivement cet historique de rappel ?',
                detail: {
                    message: 'Cette action est définitive.',
                    severity: 'warning',
                },
                validation: 'Oui, je veux supprimer cet historique de rappel',
                annulation: 'Non, je veux annuler la suppression',
            };
        }

        return {
            question: 'Êtes-vous sûr de vouloir supprimer définitivement ce rappel ?',
            detail: {
                message: 'Cette action est définitive.',
                severity: 'warning',
            },
            validation: 'Oui, je veux supprimer ce rappel',
            annulation: 'Non, je veux annuler la suppression',
        };
    }, []);

    /**
     * Génère les messages de confirmation pour la suppression multiple de rappels
     *
     * @param rappels - Liste des rappels à supprimer
     * @returns Objets de messages pour la confirmation multiple
     */
    const getMultipleMessages = React.useCallback((rappels: ReminderPublisher[]): MessagesTypes => {
        const sentCount = rappels.filter((r) => r.status === 'sent').length;
        const totalCount = rappels.length;

        if (sentCount === 0) {
            return {
                question: `Êtes-vous sûr de vouloir supprimer définitivement ces ${totalCount} rappels ?`,
                detail: {
                    message: 'Cette action est définitive.',
                    severity: 'warning',
                },
                validation: 'Oui, je veux supprimer ces rappels',
                annulation: 'Non, je veux annuler la suppression',
            };
        }
        if (sentCount === totalCount) {
            return {
                question: `Êtes-vous sûr de vouloir supprimer définitivement ces ${totalCount} historiques de rappels ?`,
                detail: {
                    message: 'Cette action est définitive.',
                    severity: 'warning',
                },
                validation: 'Oui, je veux supprimer ces historiques de rappels',
                annulation: 'Non, je veux annuler la suppression',
            };
        }
        return {
            question: `Êtes-vous sûr de vouloir supprimer définitivement ces ${totalCount} éléments (${sentCount} historiques et ${totalCount - sentCount} rappels) ?`,
            detail: {
                message: 'Cette action est définitive.',
                severity: 'warning',
            },
            validation: 'Oui, je veux supprimer ces éléments',
            annulation: 'Non, je veux annuler la suppression',
        };
    }, []);

    /**
     * Supprime un rappel fournisseur spécifique
     *
     * @param reminderPublisher - Le rappel fournisseur à supprimer
     */
    const deleteReminderPublisher = React.useCallback(
        (reminderPublisher: ReminderPublisher) => {
            if (!reminderPublisher?.id) return;

            const requestParameters: ReminderPublisherApiDeleteReminderPublisherItemRequest = {
                id: reminderPublisher?.id,
                xIdSociete,
            };

            request(() => reminderPublisherApi.deleteReminderPublisherItem(requestParameters), {
                successMessage: 'Suppression du rappel fournisseur avec succès.',
                errorMessage:
                    'Une erreur est survenue lors de la suppression du rappel fournisseur.',
                successCallback() {
                    if (from === 'details') {
                        navigate(-1);
                    } else {
                        dispatch(getReminderPublisher({}));
                    }
                },
                confirmationPromise: true,
                confirmationPromiseProps: {
                    color: theme.palette.error,
                    messages: getMessages(reminderPublisher.status),
                    loadingMessage: 'Suppression du rappel en cours...',
                },
            });
        },
        [
            xIdSociete,
            request,
            theme.palette.error,
            getMessages,
            reminderPublisherApi,
            from,
            navigate,
            dispatch,
            getReminderPublisher,
        ],
    );

    /**
     * Supprime plusieurs rappels fournisseurs simultanément
     *
     * @param reminderPublishers - Tableau de rappels fournisseurs à supprimer
     */
    const deleteMultipleReminderPublisher = React.useCallback(
        async (reminderPublishers: ReminderPublisher[]) => {
            if (reminderPublishers.length === 0) return;

            // Filtrer les rappels valides (avec ID)
            const validReminderPublishers = reminderPublishers.filter((rp) => !!rp.id);

            if (validReminderPublishers.length === 0) {
                enqueueSnackbar('Aucun rappel valide à supprimer', { variant: 'warning' });
                return;
            }

            // Demander confirmation à l'utilisateur
            try {
                // Utiliser une requête API réelle pour déclencher la confirmation
                const firstReminderId = validReminderPublishers[0].id;

                await request(
                    () =>
                        reminderPublisherApi.getReminderPublisherItem({
                            id: firstReminderId!,
                            xIdSociete,
                        }),
                    {
                        confirmationPromise: true,
                        withToaster: false, // Ne pas afficher de toast pour cette requête de confirmation
                        confirmationPromiseProps: {
                            color: theme.palette.error,
                            messages: getMultipleMessages(validReminderPublishers),
                            loadingMessage: 'Préparation de la suppression multiple...',
                        },
                    },
                );

                // Si l'utilisateur confirme, traiter la suppression en masse
                enqueueSnackbar(
                    `Début de la suppression de ${validReminderPublishers.length} rappels...`,
                    { variant: 'info' },
                );

                // Créer un tableau de promesses de suppression
                const deletePromises = validReminderPublishers.map(async (reminder) => {
                    try {
                        if (!reminder.id) return false;

                        const requestParameters: ReminderPublisherApiDeleteReminderPublisherItemRequest =
                            {
                                id: reminder.id,
                                xIdSociete,
                            };

                        await reminderPublisherApi.deleteReminderPublisherItem(requestParameters);
                        return true;
                    } catch (error) {
                        console.error(
                            `Erreur lors de la suppression du rappel ${reminder.id}:`,
                            error,
                        );
                        return false;
                    }
                });

                // Exécuter les suppressions et attendre les résultats
                const results = await Promise.all(deletePromises);

                // Compter les succès et échecs
                const successCount = results.filter(Boolean).length;
                const failureCount = validReminderPublishers.length - successCount;

                // Afficher un message récapitulatif
                if (failureCount === 0) {
                    enqueueSnackbar(
                        `Tous les rappels (${successCount}) ont été supprimés avec succès.`,
                        { variant: 'success' },
                    );
                } else if (successCount === 0) {
                    enqueueSnackbar(`Échec de la suppression de tous les rappels.`, {
                        variant: 'error',
                    });
                } else {
                    enqueueSnackbar(
                        `${successCount} rappels supprimés avec succès, ${failureCount} échecs.`,
                        { variant: 'warning' },
                    );
                }

                // Rafraîchir les données
                dispatch(getReminderPublisher({}));
            } catch (error) {
                // L'utilisateur a annulé ou une erreur est survenue au niveau de la confirmation
                console.error('Opération multiple annulée ou erreur:', error);
            }
        },
        [
            dispatch,
            enqueueSnackbar,
            getMultipleMessages,
            getReminderPublisher,
            reminderPublisherApi,
            request,
            theme.palette.error,
            xIdSociete,
        ],
    );

    return {
        deleteReminderPublisher,
        deleteMultipleReminderPublisher,
        state: requestState,
    };
};

export default useDeleteReminderPublisher;
